import React, { useMemo } from 'react';
import { CheckCircle2, PencilLine, Sparkles, ListTodo } from 'lucide-react';
import { User, DeploymentRequest } from '../../types';
import { CATEGORICAL_PALETTE, STATUS_COLORS, OTHER_SLOT_COLOR, assignCategoricalColors } from '../../utils/chartColors';
import Avatar from '../common/Avatar';

interface MyDashboardProps {
  currentUser: User;
  deployments: DeploymentRequest[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

const withinLastDays = (dateStr: string | undefined, days: number) => {
  if (!dateStr) return false;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= days * DAY_MS && t <= Date.now();
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Good morning', emoji: '🌤️' };
  if (hour < 17) return { text: 'Good afternoon', emoji: '☀️' };
  return { text: 'Good evening', emoji: '🌙' };
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  deployed: { label: 'Deployed', color: STATUS_COLORS.good },
  tl_approved: { label: 'Approved (TL)', color: CATEGORICAL_PALETTE[0] },
  pm_approved: { label: 'Approved (PM)', color: CATEGORICAL_PALETTE[0] },
  pending: { label: 'Pending Review', color: STATUS_COLORS.warning },
  rejected: { label: 'Rejected', color: STATUS_COLORS.critical },
  rolled_back: { label: 'Rolled Back', color: OTHER_SLOT_COLOR },
};

const OPEN_STATUSES = ['pending', 'tl_approved', 'pm_approved'];

// Donut built from plain stacked stroke segments — no charting library needed
// for a handful of status slices.
const StatusDonut: React.FC<{ segments: Array<{ label: string; value: number; color: string }> }> = ({ segments }) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;

  return (
    <div className="flex items-center gap-8 flex-wrap">
      <div className="relative shrink-0" style={{ width: 150, height: 150 }}>
        <svg width={150} height={150} className="-rotate-90">
          <circle cx={75} cy={75} r={radius} fill="none" stroke="#f1f0ed" strokeWidth={18} />
          {total > 0 &&
            segments.map((seg) => {
              const fraction = seg.value / total;
              const dash = fraction * circumference;
              const offset = circumference * (1 - cumulative);
              cumulative += fraction;
              return (
                <circle
                  key={seg.label}
                  cx={75}
                  cy={75}
                  r={radius}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={18}
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  strokeDashoffset={offset}
                />
              );
            })}
        </svg>
      </div>
      <div className="space-y-2.5">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center justify-between gap-8 text-sm">
            <span className="flex items-center gap-2 text-gray-600">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
              {seg.label}
            </span>
            <span className="font-semibold text-gray-900 tabular-nums">{seg.value}</span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-8 text-sm pt-2.5 mt-1 border-t border-gray-100">
          <span className="text-gray-600">Total</span>
          <span className="font-semibold text-gray-900 tabular-nums">{total}</span>
        </div>
      </div>
    </div>
  );
};

const SummaryTile: React.FC<{ icon: React.ElementType; iconClass: string; textClass: string; value: number; label: string; caption: string }> = ({
  icon: Icon, iconClass, textClass, value, label, caption,
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5">
    <div className="flex items-center gap-3">
      <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${iconClass}`}>
        <Icon className="w-[18px] h-[18px]" />
      </span>
      <span className="flex items-baseline gap-1.5">
        <span className={`text-2xl font-bold tabular-nums ${textClass}`}>{value}</span>
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </span>
    </div>
    <p className="text-xs text-gray-500 mt-2">{caption}</p>
  </div>
);

// A personal, per-user landing view — the shared "Dashboard" stays org/role
// focused; this answers "what's mine, and what does my team look like"
// the way the requested reference design does for a generic project tool.
const MyDashboard: React.FC<MyDashboardProps> = ({ currentUser, deployments }) => {
  const greeting = getGreeting();
  const firstName = currentUser.name?.split(' ')[0] || currentUser.name;

  const myDeployments = useMemo(() => {
    switch (currentUser.role) {
      case 'developer':
        return deployments.filter((d) => d.requestedBy?.id === currentUser.id);
      case 'tl':
        return deployments.filter(
          (d) =>
            d.requestedBy?.id === currentUser.id ||
            d.tlApprovals?.some((a) => a.tl?.id === currentUser.id)
        );
      case 'pm':
        return deployments.filter(
          (d) =>
            d.requestedBy?.id === currentUser.id ||
            d.pmApprovals?.some((a) => a.pm?.id === currentUser.id)
        );
      case 'devops':
        return deployments.filter(
          (d) => d.deployment?.deployedBy?.id === currentUser.id || d.status === 'pm_approved'
        );
      case 'tester':
        return deployments.filter((d) => d.testers?.some((t) => t.id === currentUser.id));
      default:
        // admin has no personal request queue — their "mine" is the org view
        return deployments;
    }
  }, [deployments, currentUser]);

  const doneCount = myDeployments.filter(
    (d) => d.status === 'deployed' && withinLastDays(d.deployment?.deployedAt || d.updatedAt, 7)
  ).length;
  const updatedCount = myDeployments.filter((d) => withinLastDays(d.updatedAt, 7)).length;
  const newCount = myDeployments.filter((d) => withinLastDays(d.requestedAt, 7)).length;
  const openCount = myDeployments.filter((d) => OPEN_STATUSES.includes(d.status || '')).length;

  const statusSegments = useMemo(() => {
    const counts: Record<string, number> = {};
    myDeployments.forEach((d) => {
      const key = d.status || 'pending';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .filter(([, value]) => value > 0)
      .map(([status, value]) => ({
        label: STATUS_LABELS[status]?.label || status,
        color: STATUS_LABELS[status]?.color || OTHER_SLOT_COLOR,
        value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [myDeployments]);

  const showTeamWorkload = ['tl', 'pm', 'devops', 'admin'].includes(currentUser.role);

  const teamWorkload = useMemo(() => {
    if (!showTeamWorkload) return [];
    const counts: Record<string, { name: string; id?: string; value: number }> = {};
    deployments.forEach((d) => {
      const name = d.requestedBy?.name || 'Unassigned';
      const id = d.requestedBy?.id;
      const key = id || name;
      if (!counts[key]) counts[key] = { name, id, value: 0 };
      counts[key].value += 1;
    });
    const entries = Object.values(counts).filter((e) => e.value > 0);
    const colored = assignCategoricalColors(entries);
    return colored.slice(0, 6);
  }, [deployments, showTeamWorkload]);

  const workloadTotal = teamWorkload.reduce((s, w) => s + w.value, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          {greeting.emoji} {greeting.text}, {firstName}
        </h1>
        <p className="text-gray-600 mt-1">Here's what's happening with your deployments.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryTile icon={CheckCircle2} iconClass="bg-emerald-100 text-emerald-600" textClass="text-emerald-600" value={doneCount} label="done" caption="in the last 7 days" />
        <SummaryTile icon={PencilLine} iconClass="bg-blue-100 text-blue-600" textClass="text-blue-600" value={updatedCount} label="updated" caption="in the last 7 days" />
        <SummaryTile icon={Sparkles} iconClass="bg-violet-100 text-violet-600" textClass="text-violet-600" value={newCount} label="new" caption="in the last 7 days" />
        <SummaryTile icon={ListTodo} iconClass="bg-amber-100 text-amber-600" textClass="text-amber-600" value={openCount} label="open" caption="awaiting action right now" />
      </div>

      <div className={`grid grid-cols-1 ${showTeamWorkload ? 'lg:grid-cols-2' : ''} gap-6`}>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900">Status summary</h2>
          <p className="text-sm text-gray-500 mb-4">A snapshot of where your deployments stand.</p>
          {myDeployments.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">Nothing to show yet.</div>
          ) : (
            <StatusDonut segments={statusSegments} />
          )}
        </div>

        {showTeamWorkload && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900">Team workload</h2>
            <p className="text-sm text-gray-500 mb-4">Requests submitted, by team member.</p>
            {teamWorkload.length === 0 ? (
              <div className="text-sm text-gray-400 py-8 text-center">No requests yet.</div>
            ) : (
              <div>
                <div className="flex items-center gap-3 text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
                  <span className="w-[calc(1.75rem+7rem+0.75rem)] shrink-0">Assignee</span>
                  <span className="flex-1">Work distribution</span>
                  <span className="w-8 text-right shrink-0">Count</span>
                </div>
                <div className="space-y-4">
                  {teamWorkload.map((w) => {
                    const percent = workloadTotal > 0 ? Math.round((w.value / workloadTotal) * 100) : 0;
                    return (
                      <div key={w.name} className="flex items-center gap-3">
                        <Avatar name={w.name} userId={w.id} size="sm" />
                        <span className="text-sm text-gray-700 w-28 truncate shrink-0">{w.name}</span>
                        <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${percent}%`, backgroundColor: w.color }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-8 text-right shrink-0">{w.value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDashboard;
